import { NATIVE_OR_SIM } from '../proximity/native/simPhone';

import { APP_BUILD_VERSION } from '../query/cacheVersion';
import { getSupabase } from '../../supabaseClient';
import {
  FIND_LOG_FLUSH_INTERVAL_MS,
  FIND_LOG_FLUSH_SIZE_THRESHOLD,
  FIND_REMOTE_LOGGING,
} from '../../config/findRemoteLogging';
import { readStoredFindGroupId } from '../findSelectedGroupStorage';
import { createLogBuffer } from './buffer';
import { meetsMinLevel } from './levels';
import {
  clearPersistedEntries,
  persistPendingEntries,
  persistPendingEntriesNow,
  restorePersistedEntries,
} from './persist';
import { resetSourceThrottle, shouldThrottleSource } from './throttle';
import { isAuthRejection, shouldDropBatch } from './uploadFailure';
import {
  uploadFindLogBatch,
  getFindRemoteLogUploadDiagnostics,
  recordFindRemoteLogUploadFailure,
} from './transport';

/** One flush failure per app session reaches app_logs (see the flush loop). */
let reportedUploadOutage = false;

/** Single gate for "should this surface log at all" — see FIND_REMOTE_LOGGING.nativeOnly. */
function remoteLoggingActive() {
  if (!FIND_REMOTE_LOGGING.enabled) {
    return false;
  }
  return !FIND_REMOTE_LOGGING.nativeOnly || NATIVE_OR_SIM;
}

let pageSessionId = null;
let userId = null;
let groupId = null;
let pageContextActive = false;
let engineSessionLogged = false;
let flushTimer = null;
let flushInFlight = false;
let restorePromise = null;

const buffer = createLogBuffer({
  onPendingChange(entries) {
    persistPendingEntries(entries);
    if (entries.length >= FIND_LOG_FLUSH_SIZE_THRESHOLD) {
      void flushFindRemoteLogs('size-threshold');
    }
  },
});

function scheduleFlushTimer() {
  if (flushTimer != null) {
    clearInterval(flushTimer);
  }
  flushTimer = setInterval(() => {
    void flushFindRemoteLogs('interval');
  }, FIND_LOG_FLUSH_INTERVAL_MS);
}

function clearFlushTimer() {
  if (flushTimer != null) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
}

function rowForUpload(entry) {
  const payload =
    entry.repeat_count != null && entry.repeat_count > 1
      ? { ...entry.payload, repeat_count: entry.repeat_count }
      : (entry.payload ?? {});
  return {
    session_id: entry.session_id,
    user_id: entry.user_id,
    group_id: entry.group_id ?? null,
    client_ts: entry.client_ts,
    seq: entry.seq,
    level: entry.level,
    source: entry.source ?? 'js',
    event_name: entry.event_name,
    payload,
    app_version: entry.app_version ?? APP_BUILD_VERSION,
  };
}

/**
 * Returns `{ token, refreshed }`. `refreshed` distinguishes "we got a new token" from
 * "we could not ask" — offline, refreshSession() fails exactly as it does for a session
 * that is genuinely dead, and both fall through to the same stale cached token. The
 * caller needs that difference to decide whether a 401/403 is permanent.
 */
async function getAccessToken({ tryRefresh = false } = {}) {
  try {
    const supabase = getSupabase();
    if (tryRefresh) {
      const { data: refreshed, error } = await supabase.auth.refreshSession();
      if (!error && refreshed?.session?.access_token) {
        return { token: refreshed.session.access_token, refreshed: true };
      }
    }
    const { data } = await supabase.auth.getSession();
    return { token: data?.session?.access_token ?? null, refreshed: false };
  } catch {
    return { token: null, refreshed: false };
  }
}

/**
 * Rows are inserted under RLS `user_id = auth.uid()`. A tail persisted by a previous
 * account on this device can never upload, so it is dropped instead of poisoning the queue.
 */
async function ensureRestored() {
  if (restorePromise) {
    return restorePromise;
  }
  restorePromise = restorePersistedEntries().then((restored) => {
    const ownerId = userId;
    const mine = ownerId ? restored.filter((row) => row.user_id === ownerId) : restored;
    const foreign = restored.length - mine.length;
    buffer.loadRestored(mine);
    if (foreign > 0) {
      findRemoteLog(
        'warn',
        'find.log.restore_discarded_foreign',
        {
          discarded: foreign,
          restored: mine.length,
        },
        { skipThrottle: true },
      );
    }
  });
  return restorePromise;
}

/** Page-level log context — survives proximity engine restarts within a Find visit. */
export async function ensureFindRemoteLogPageContext({ userId: nextUserId, groupId: nextGroupId }) {
  if (!remoteLoggingActive() || !nextUserId) {
    return;
  }

  userId = nextUserId;
  groupId = nextGroupId ?? groupId ?? readStoredFindGroupId(nextUserId) ?? null;

  if (!pageContextActive) {
    pageSessionId = crypto.randomUUID();
    pageContextActive = true;
    await ensureRestored();
    scheduleFlushTimer();
    findRemoteLog(
      'info',
      'find.page.context.start',
      {
        sessionId: pageSessionId,
        groupId,
        groupSource: nextGroupId ? 'prop' : groupId ? 'localStorage' : null,
      },
      { skipThrottle: true },
    );
  } else if (nextGroupId && nextGroupId !== groupId) {
    groupId = nextGroupId;
  }
}

export async function shutdownFindRemoteLogPageContext() {
  if (!pageContextActive) {
    return;
  }
  findRemoteLog(
    'info',
    'find.page.context.stop',
    { sessionId: pageSessionId },
    {
      skipThrottle: true,
    },
  );
  clearFlushTimer();
  await flushFindRemoteLogs('page-shutdown');
  pageContextActive = false;
  pageSessionId = null;
  userId = null;
  groupId = null;
  engineSessionLogged = false;
  resetSourceThrottle();
}

/** Proximity engine started — log only; does not reset page log context. */
export async function startFindRemoteLogSession({
  sessionId: engineSessionId,
  userId: nextUserId,
  groupId: nextGroupId,
}) {
  await ensureFindRemoteLogPageContext({ userId: nextUserId, groupId: nextGroupId });
  engineSessionLogged = true;
  findRemoteLog(
    'info',
    'proximity.engine.session.start',
    { engineSessionId, groupId: nextGroupId ?? groupId },
    { skipThrottle: true },
  );
}

/** Proximity engine stopped — log only; page log context keeps buffering. */
export async function stopFindRemoteLogSession({ flush = false } = {}) {
  if (!pageContextActive || !engineSessionLogged) {
    return;
  }
  engineSessionLogged = false;
  findRemoteLog('info', 'proximity.engine.session.stop', { groupId }, { skipThrottle: true });
  if (flush) {
    await flushFindRemoteLogs('engine-stop');
  }
}

export async function flushFindRemoteLogs(reason = 'manual') {
  if (!remoteLoggingActive() || flushInFlight) {
    return false;
  }
  if (buffer.pendingCount() === 0) {
    return true;
  }

  flushInFlight = true;
  try {
    let { token } = await getAccessToken();
    if (!token) {
      ({ token } = await getAccessToken({ tryRefresh: true }));
    }
    if (!token) {
      recordFindRemoteLogUploadFailure(
        'no-auth-token',
        0,
        'getSession returned no access_token',
        buffer.pendingCount(),
      );
      return false;
    }

    while (buffer.pendingCount() > 0) {
      const batch = buffer.takeBatch(FIND_LOG_FLUSH_SIZE_THRESHOLD);
      if (batch.length === 0) {
        break;
      }
      const rows = batch.map(rowForUpload);
      let result = await uploadFindLogBatch(rows, token);
      let reauthFailed = false;
      if (!result.ok && isAuthRejection(result.status) && token) {
        const attempt = await getAccessToken({ tryRefresh: true });
        reauthFailed = !attempt.refreshed;
        if (attempt.token && attempt.token !== token) {
          token = attempt.token;
          result = await uploadFindLogBatch(rows, token);
          reauthFailed = false;
        }
      }
      if (!result.ok) {
        // See uploadFailure.js — an auth rejection we could not re-authenticate through
        // is transient, not permanent. The ring buffer is capacity-bounded, so keeping
        // the rows cannot grow the queue without limit.
        const permanent = shouldDropBatch(result.status, reauthFailed);
        if (!permanent) {
          buffer.prependRestored(batch);
        }
        const diag = getFindRemoteLogUploadDiagnostics();
        findRemoteLog(
          'warn',
          'find.log.upload_failed',
          {
            reason,
            status: result.status,
            mode: result.mode ?? null,
            body: diag?.body ?? null,
            batchSize: batch.length,
            dropped: permanent,
            reauthFailed,
          },
          { skipThrottle: true },
        );
        // warn, not error: find.log.upload_failed already records it, and app_logs mirrors console.error - 1,755 rows of
        // logging about logging on the 2026-10-09..11 weekend.
        // Once per app session - or for a batch that is gone for good - it still reaches app_logs (via console.error), so a
        // total outage of the Find log (an RLS or auth regression) is not invisible: the failure rows above sit in the
        // same buffer that cannot upload (2026-10-12 audit).
        const report = permanent || !reportedUploadOutage ? console.error : console.warn;
        if (!permanent) reportedUploadOutage = true;
        report('[findRemoteLog] flush failed', {
          reason,
          ...result,
          dropped: permanent,
          pending: buffer.pendingCount(),
        });
        if (permanent) {
          // Batch is unrecoverable; keep draining so healthy rows behind it still land.
          continue;
        }
        return false;
      }
      persistPendingEntries(buffer.peekAll());
    }

    if (buffer.pendingCount() === 0) {
      await clearPersistedEntries();
    }
    return true;
  } finally {
    flushInFlight = false;
  }
}

export function persistFindRemoteLogsNow() {
  return persistPendingEntriesNow(buffer.peekAll());
}

/**
 * @param {'debug'|'info'|'warn'|'error'} level
 * @param {string} eventName dotted event name
 * @param {Record<string, unknown>} [payload]
 * @param {{ source?: string, throttleKey?: string, skipThrottle?: boolean }} [options]
 */
export function findRemoteLog(level, eventName, payload = {}, options = {}) {
  if (!remoteLoggingActive() || !pageContextActive || !pageSessionId || !userId) {
    return;
  }
  if (!meetsMinLevel(level, FIND_REMOTE_LOGGING.minLevel)) {
    return;
  }

  const throttleKey = options.throttleKey ?? eventName;
  if (!options.skipThrottle && shouldThrottleSource(throttleKey)) {
    return;
  }

  buffer.append({
    session_id: pageSessionId,
    user_id: userId,
    group_id: groupId,
    client_ts: Date.now(),
    level,
    source: options.source ?? 'js',
    event_name: eventName,
    payload,
    app_version: APP_BUILD_VERSION,
  });
}

export function updateFindRemoteLogGroup(nextGroupId) {
  groupId = nextGroupId ?? null;
  findRemoteLog('info', 'find.session.group_change', { groupId }, { skipThrottle: true });
}

export function getFindRemoteLogSessionId() {
  return pageSessionId;
}

export { getFindRemoteLogUploadDiagnostics };
